import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { AutoComplete } from "primereact/autocomplete";
import "./AddKudosButton.css";
import { addKudos } from "../../services/wisdomServices/growthService";
import { getTeamMembers } from "../../services/wisdomServices/managerService";
import { getSessionItem } from "../../utils/helper/session";

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 255;

function AddKudosButton({ onCreated }) {
  const loggedUser = getSessionItem("User");

  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  const searchTimeout = useRef(null);

  const searchUsers = (event) => {
    clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await getTeamMembers(
          loggedUser.id,
          false,
          event.query,
          1,
          100
        );

        const mappedUsers = (response?.data ?? [])
          .filter((user) => user.id !== loggedUser.id)
          .map((user) => ({
            id: user.id,
            name: user.fullName,
            email: user.username
          }));

        setUsers(mappedUsers);
      } catch (error) {
        console.error("Failed to search users:", error);
        setUsers([]);
      }
    }, 500);
  };

  const closeModal = () => {
    setVisible(false);
    setSelectedUser(null);
    setUsers([]);
    setTitle("");
    setMessage("");
  };

  const handleSubmit = async () => {
    if (!selectedUser?.id || !title.trim() || !message.trim()) return;

    try {
      setLoading(true);

      await addKudos({
        recipientId: selectedUser.id,
        reason: title.trim(),
        message: message.trim()
      });

      closeModal();
      onCreated?.();
    } catch (error) {
      console.error("Failed to add Kudos:", error);
    } finally {
      setLoading(false);
    }
  };

  const isValid =
    selectedUser?.id &&
    title.trim() &&
    message.trim();

  return (
    <>
      <Button
        type="button"
        label="Add Kudos"
        icon="pi pi-star"
        outlined
        className="summary-kudos-action-button"
        onClick={() => setVisible(true)}
      />

      <Dialog
        header="Add new Kudos"
        visible={visible}
        className="kudos-dialog"
        modal
        draggable={false}
        onHide={closeModal}
      >
        <div className="summary-growth-modal">
          <div className="summary-growth-modal-row">
            <div className="summary-growth-field">
              <label htmlFor="kudos-recipient">
                User
              </label>

              <AutoComplete
                id="kudos-recipient"
                value={selectedUser}
                suggestions={users}
                completeMethod={searchUsers}
                field="name"
                forceSelection
                appendTo="self"
                placeholder="Search user..."
                onChange={(e) => setSelectedUser(e.value)}
              />
            </div>

            <div className="summary-growth-field">
              <label htmlFor="kudos-title">
                Title <span className="char-counter">({MAX_TITLE_LENGTH - title.length})</span>
              </label>

              <InputText
                id="kudos-title"
                value={title}
                max={100}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter the title..."
              />
            </div>
          </div>

          <div className="summary-growth-field">
            <label htmlFor="kudos-message">
              Message <span className="char-counter">({MAX_DESCRIPTION_LENGTH - message.length})</span>
            </label>

            <InputTextarea
              id="kudos-message"
              value={message}
              maxLength={255}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              autoResize
              placeholder="Write your Kudos message..."
            />
          </div>

          <div className="summary-growth-modal-actions">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              text
              onClick={closeModal}
            />

            <Button
              type="button"
              label="Send Kudos"
              icon="pi pi-send"
              loading={loading}
              disabled={!isValid}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default AddKudosButton;